import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Phone, Loader2, Building2, Music, Video, Mic2, Radio, Headphones, Camera, Palette, Wrench, AtSign } from 'lucide-react';
import { FullBrandLogo } from '../components/Logo';
import NeighborhoodSelect from '../components/NeighborhoodSelect';

const CREATOR_TYPES = [
  { value: 'artist', label: 'Artist (Music)', icon: Music, description: 'Upload music, manage tracks, view charts' },
  { value: 'content_creator', label: 'Content Creator', icon: Video, description: 'Video-first influencer — vlogs, skits, comedy, lifestyle' },
  { value: 'video_creator', label: 'Video Creator', icon: Video, description: 'Upload videos, create reels' },
  { value: 'podcaster', label: 'Podcaster', icon: Mic2, description: 'Create podcast shows, upload episodes' },
  { value: 'streamer', label: 'Streamer', icon: Radio, description: 'Go live, stream content' },
  { value: 'producer', label: 'Producer', icon: Headphones, description: 'Upload beats, licensing' },
  { value: 'videographer', label: 'Videographer', icon: Camera, description: 'Video production services' },
  { value: 'engineer', label: 'Engineer', icon: Wrench, description: 'Audio engineering services' },
  { value: 'graphic_designer', label: 'Graphic Designer', icon: Palette, description: 'Design portfolio & services' },
  { value: 'studio', label: 'Studio', icon: Building2, description: 'Studio booking & services' },
];

const ORG_TYPES = [
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'community_group', label: 'Community Group' },
  { value: 'school', label: 'School' },
  { value: 'church', label: 'Church' },
  { value: 'youth_program', label: 'Youth Program' },
  { value: 'small_business', label: 'Small Business' },
  { value: 'other', label: 'Other' }
];

const formatPhoneDisplay = (digits) => {
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6,10)}`;
};

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);

  const preSelectedRole = searchParams.get('role') || 'listener';

  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '', name: '', handle: '', phone: '',
    accountType: preSelectedRole === 'organization' ? 'organization' : (preSelectedRole !== 'listener' ? 'creator' : 'listener'),
    creatorType: preSelectedRole !== 'listener' && preSelectedRole !== 'organization' ? preSelectedRole : '',
    agreedToTerms: false,
    neighborhood_id: '',
    org_type: '', mission: '', city: '', state: '', neighborhood: '', website: ''
  });

  useEffect(() => {
    if (preSelectedRole && preSelectedRole !== 'listener') {
      if (preSelectedRole === 'organization') {
        setForm(prev => ({ ...prev, accountType: 'organization' }));
      } else {
        setForm(prev => ({ ...prev, accountType: 'creator', creatorType: preSelectedRole }));
      }
    }
  }, [preSelectedRole]);

  const requiresPhone = form.accountType === 'creator';

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!form.agreedToTerms) {
      toast.error('Please agree to the Terms & Community Guidelines');
      return;
    }
    if (!form.neighborhood_id && form.accountType !== 'organization') {
      toast.error('Neighborhood is required.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!form.handle || form.handle.length < 3) {
      toast.error('Handle must be at least 3 characters');
      return;
    }
    if (form.accountType === 'creator' && !form.creatorType) {
      toast.error('Please select a creator type');
      return;
    }
    if (requiresPhone && form.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      let role = 'listener';
      if (form.accountType === 'creator') role = form.creatorType;
      else if (form.accountType === 'organization') role = 'organization';

      const orgData = form.accountType === 'organization' ? {
        handle: form.handle, org_type: form.org_type, mission: form.mission,
        city: form.city, state: form.state,
        neighborhood: form.neighborhood || '', website: form.website || ''
      } : null;

      await register(
        form.email, form.password, form.name, role,
        true, '1.0.0',
        requiresPhone ? form.phone.replace(/\D/g, '') : '',
        orgData, form.handle, form.neighborhood_id
      );

      toast.success('Account created successfully!');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/home';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoftSignup = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login`);
      const data = await res.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.info(data.detail || 'Microsoft Sign-In not yet configured by admin.');
      }
    } catch {
      toast.info('Microsoft Sign-In will be available soon.');
    }
  };

  const u = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="signup-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <FullBrandLogo size="lg" className="drop-shadow-xl" />
          </Link>
          <h1 className="font-heading text-4xl font-bold uppercase mb-2">Create Account</h1>
          <p className="text-muted-foreground">Join the DMV's music community</p>
        </div>

        <Card className="glass-card border-border p-6">
          {/* OAuth Quick Sign-Up */}
          <div className="space-y-3 mb-6">
            <button
              type="button"
              onClick={handleGoogleSignup}
              className="w-full flex items-center justify-center gap-3 h-11 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors text-sm font-medium"
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
            <button
              type="button"
              onClick={() => toast.info('Apple Sign-In coming soon! Use email signup for now.')}
              className="w-full flex items-center justify-center gap-3 h-11 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors text-sm font-medium opacity-70"
              data-testid="apple-signup-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Continue with Apple
              <span className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded ml-1">Soon</span>
            </button>
            <button
              type="button"
              onClick={handleMicrosoftSignup}
              className="w-full flex items-center justify-center gap-3 h-11 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors text-sm font-medium"
              data-testid="microsoft-signup-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
                <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
                <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
              </svg>
              Continue with Microsoft
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-3 text-muted-foreground uppercase tracking-wider">or sign up with email</span>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleRegister} className="space-y-4">
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="r-name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="r-name" type="text" placeholder="Your name" value={form.name}
                  onChange={e => u('name', e.target.value)} className="pl-10" required data-testid="register-name-input" />
              </div>
            </div>

            {/* Handle */}
            <div className="space-y-1.5">
              <Label htmlFor="r-handle">@handle</Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="r-handle" type="text" placeholder="yourhandle" value={form.handle}
                  onChange={e => u('handle', e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
                  className="pl-10" required maxLength={30} data-testid="register-handle-input" />
              </div>
              <p className="text-xs text-muted-foreground">Your unique public profile URL</p>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="r-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="r-email" type="email" placeholder="you@example.com" value={form.email}
                  onChange={e => u('email', e.target.value)} className="pl-10" required data-testid="register-email-input" />
              </div>
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="r-pass">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="r-pass" type="password" placeholder="Min 6 chars" value={form.password}
                    onChange={e => u('password', e.target.value)} className="pl-10" required minLength={6}
                    data-testid="register-password-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="r-confirm">Confirm</Label>
                <Input id="r-confirm" type="password" placeholder="Re-enter" value={form.confirmPassword}
                  onChange={e => u('confirmPassword', e.target.value)} required minLength={6}
                  data-testid="register-confirm-password-input" />
              </div>
            </div>

            {/* Account Type */}
            <div className="space-y-2">

            {/* Neighborhood (required for non-org) */}
            {form.accountType !== 'organization' && (
              <NeighborhoodSelect
                value={form.neighborhood_id}
                onChange={(id, hood) => setForm(prev => ({ ...prev, neighborhood_id: id, neighborhood: hood?.name || '' }))}
                required
                error={!form.neighborhood_id && form.agreedToTerms ? '' : ''}
              />
            )}

            </div>
            <div className="space-y-2">
              <Label>I am a...</Label>
              <RadioGroup value={form.accountType}
                onValueChange={v => setForm(prev => ({ ...prev, accountType: v, creatorType: '' }))}
                className="grid grid-cols-3 gap-2"
              >
                {[
                  { value: 'listener', label: 'Listener' },
                  { value: 'creator', label: 'Creator' },
                  { value: 'organization', label: 'Org', icon: Building2 }
                ].map(t => (
                  <div key={t.value} className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer">
                    <RadioGroupItem value={t.value} id={`type-${t.value}`} data-testid={`type-${t.value}`} />
                    <Label htmlFor={`type-${t.value}`} className="cursor-pointer text-sm flex items-center gap-1">
                      {t.icon && <t.icon className="w-3 h-3" />}
                      {t.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Creator Type */}
            {form.accountType === 'creator' && (
              <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border">
                <Label>Select your creator type *</Label>
                <Select value={form.creatorType} onValueChange={v => u('creatorType', v)}>
                  <SelectTrigger className="bg-muted/30 border-border" data-testid="creator-type-select">
                    <SelectValue placeholder="Choose your creator type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CREATOR_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" /><span>{type.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.creatorType && (
                  <p className="text-xs text-muted-foreground">
                    {CREATOR_TYPES.find(t => t.value === form.creatorType)?.description}
                  </p>
                )}
                <div className="space-y-1.5 mt-3">
                  <Label>Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="tel" placeholder="(555) 555-5555"
                      value={formatPhoneDisplay(form.phone)}
                      onChange={e => u('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-10 bg-muted/30 border-border" data-testid="register-phone-input" />
                  </div>
                  <p className="text-xs text-muted-foreground">Required for verification</p>
                </div>
              </div>
            )}

            {/* Organization Fields */}
            {form.accountType === 'organization' && (
              <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border">
                <Label>Organization Type *</Label>
                <Select value={form.org_type} onValueChange={v => u('org_type', v)}>
                  <SelectTrigger className="bg-muted/30 border-border">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORG_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="space-y-1.5">
                  <Label>Mission Statement *</Label>
                  <Textarea placeholder="What does your organization do?" value={form.mission}
                    onChange={e => u('mission', e.target.value)} className="bg-muted/30 border-border" maxLength={500} rows={3} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>City *</Label>
                    <Input placeholder="Washington DC" value={form.city}
                      onChange={e => u('city', e.target.value)} className="bg-muted/30 border-border" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>State *</Label>
                    <Input placeholder="DC" value={form.state}
                      onChange={e => u('state', e.target.value)} className="bg-muted/30 border-border" maxLength={2} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Neighborhood (optional)</Label>
                  <Input placeholder="Southeast" value={form.neighborhood}
                    onChange={e => u('neighborhood', e.target.value)} className="bg-muted/30 border-border" />
                </div>
                <div className="space-y-1.5">
                  <Label>Website URL (optional)</Label>
                  <Input placeholder="https://yourorg.org" value={form.website}
                    onChange={e => u('website', e.target.value)} className="bg-muted/30 border-border" />
                </div>
              </div>
            )}

            {/* Terms Checkbox */}
            <div className="flex items-start space-x-2">
              <Checkbox id="terms" checked={form.agreedToTerms}
                onCheckedChange={checked => u('agreedToTerms', checked)}
                data-testid="register-terms-checkbox" />
              <label htmlFor="terms" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                I agree to the{' '}
                <Link to="/terms" className="text-orange-400 hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/community-guidelines" className="text-orange-400 hover:underline">Community Guidelines</Link>
              </label>
            </div>

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-foreground font-bold h-11"
              disabled={loading} data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </form>

          {/* Yahoo/AOL Helper */}
          <div className="mt-4 p-3 rounded-lg bg-muted/20 border border-border">
            <p className="text-xs text-muted-foreground text-center">
              <span className="font-medium text-muted-foreground">Yahoo or AOL user?</span> Use your Yahoo/AOL email address in the email field above.
            </p>
          </div>
        </Card>

        {/* Switch to Login */}
        <p className="text-center mt-6 text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-orange-400 hover:underline font-medium" data-testid="switch-to-login">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
